import { FC, useState, useMemo } from "react";
import {
  Modal,
  Table,
  Select,
  Button,
  InputNumber,
  Tag,
  Divider,
  Empty,
  Spin,
  Input,
} from "antd";
import { Calculator, Package, Ruler, Search, DollarSign, Clock } from "lucide-react";
import { useGetInventoryTemplates } from "~/services/inventory-templates";
import { InventoryTemplate } from "~/services/inventory-templates/inventory-templates.types";
import { useGetPackingMetrics, PackingMetric } from "~/services/service-metrics";
import { PackingTypes } from "~/utils/constants";

interface InventoryTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onAddItems?: (items: SelectedTemplateItem[]) => void;
}

export interface SelectedTemplateItem {
  template: InventoryTemplate;
  packingType: string;
  packingMetric?: PackingMetric;
  quantity: number;
  materialCost: number;
  laborCost: number;
  totalCost: number;
  estimatedMinutes: number;
}

export const InventoryTemplateModal: FC<InventoryTemplateModalProps> = ({
  open,
  onClose,
  onAddItems,
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedTemplateItem>>(new Map());
  const [hourlyRate, setHourlyRate] = useState<number>(90);

  const { data: templatesData, isLoading: isLoadingTemplates } = useGetInventoryTemplates();
  const { data: packingMetrics, isLoading: isLoadingMetrics } = useGetPackingMetrics();

  // Handle both paginated and direct array responses
  const templates = useMemo(() => {
    if (!templatesData) return [];
    if (Array.isArray(templatesData)) return templatesData;
    if (templatesData.data && Array.isArray(templatesData.data)) return templatesData.data;
    return [];
  }, [templatesData]);

  // Filter templates by search
  const filteredTemplates = useMemo(() => {
    if (!searchText) return templates;
    const lower = searchText.toLowerCase();
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(lower) ||
        t.description?.toLowerCase().includes(lower) ||
        t.medium?.toLowerCase().includes(lower)
    );
  }, [templates, searchText]);

  // Get suitable packing types for a template based on dimensions
  const getSuitablePackingTypes = (template: InventoryTemplate): PackingMetric[] => {
    if (!packingMetrics) return [];
    if (!template.length || !template.width || !template.height) return packingMetrics;

    return packingMetrics.filter((metric) => {
      if (!metric.maxLength && !metric.maxWidth && !metric.maxHeight) return true;
      return (
        (!metric.maxLength || template.length! <= Number(metric.maxLength)) &&
        (!metric.maxWidth || template.width! <= Number(metric.maxWidth)) &&
        (!metric.maxHeight || template.height! <= Number(metric.maxHeight))
      );
    });
  };

  // Calculate costs for a template with selected packing type
  const calculateCosts = (
    template: InventoryTemplate,
    packingType: string,
    quantity: number
  ): Omit<SelectedTemplateItem, "template" | "packingType" | "quantity"> => {
    const metric = packingMetrics?.find((m) => m.packingType === packingType);
    if (!metric || !template.length || !template.width || !template.height) {
      return { packingMetric: undefined, materialCost: 0, laborCost: 0, totalCost: 0, estimatedMinutes: 0 };
    }

    // Calculate surface area with 5% buffer
    const bufferedL = template.length * 1.05;
    const bufferedW = template.width * 1.05;
    const bufferedH = template.height * 1.05;
    const surfaceAreaSqFt =
      (2 * (bufferedL * bufferedW + bufferedL * bufferedH + bufferedW * bufferedH)) / 144;

    // Material cost per item
    const materialCostPerItem = surfaceAreaSqFt * Number(metric.baseRate);

    // Labor cost per item
    const estimatedMinutesPerItem = Number(metric.estimatedMinutesPerItem) || 0;
    const laborCostPerItem = (estimatedMinutesPerItem / 60) * hourlyRate;

    // Total per item (with minimum charge)
    const totalPerItem = Math.max(
      materialCostPerItem + laborCostPerItem,
      Number(metric.minimumCharge) || 0
    );

    return {
      packingMetric: metric,
      materialCost: materialCostPerItem * quantity,
      laborCost: laborCostPerItem * quantity,
      totalCost: totalPerItem * quantity,
      estimatedMinutes: estimatedMinutesPerItem * quantity,
    };
  };

  const handleSelectTemplate = (template: InventoryTemplate, packingType: string) => {
    const newSelected = new Map(selectedItems);

    if (!packingType) {
      newSelected.delete(template.id);
    } else {
      const quantity = selectedItems.get(template.id)?.quantity || 1;
      const costs = calculateCosts(template, packingType, quantity);
      newSelected.set(template.id, {
        template,
        packingType,
        quantity,
        ...costs,
      });
    }

    setSelectedItems(newSelected);
  };

  const handleQuantityChange = (templateId: string, quantity: number) => {
    const existing = selectedItems.get(templateId);
    if (!existing) return;

    const costs = calculateCosts(existing.template, existing.packingType, quantity);
    const newSelected = new Map(selectedItems);
    newSelected.set(templateId, {
      ...existing,
      quantity,
      ...costs,
    });
    setSelectedItems(newSelected);
  };

  const handleAddToEstimate = () => {
    const items = Array.from(selectedItems.values());
    onAddItems?.(items);
    setSelectedItems(new Map());
    onClose();
  };

  // Calculate totals
  const totals = useMemo(() => {
    const items = Array.from(selectedItems.values());
    return items.reduce(
      (acc, item) => ({
        items: acc.items + item.quantity,
        materialCost: acc.materialCost + item.materialCost,
        laborCost: acc.laborCost + item.laborCost,
        totalCost: acc.totalCost + item.totalCost,
        totalMinutes: acc.totalMinutes + item.estimatedMinutes,
      }),
      { items: 0, materialCost: 0, laborCost: 0, totalCost: 0, totalMinutes: 0 }
    );
  }, [selectedItems]);

  const isLoading = isLoadingTemplates || isLoadingMetrics;

  const columns = [
    {
      title: "Template",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: InventoryTemplate) => (
        <div>
          <div className="font-medium">{name}</div>
          {record.medium && <div className="text-xs text-gray-500">{record.medium}</div>}
        </div>
      ),
    },
    {
      title: "Dimensions",
      key: "dimensions",
      width: 120,
      render: (_: any, record: InventoryTemplate) => (
        <span className="text-sm">
          {record.length && record.width && record.height
            ? `${record.length}×${record.width}×${record.height}"`
            : <span className="text-gray-400">No dims</span>}
        </span>
      ),
    },
    {
      title: "Packing Type",
      key: "packingType",
      width: 180,
      render: (_: any, record: InventoryTemplate) => {
        const selected = selectedItems.get(record.id);
        const suitable = getSuitablePackingTypes(record);

        return (
          <Select
            size="small"
            className="w-full"
            value={selected?.packingType || undefined}
            onChange={(value) => handleSelectTemplate(record, value)}
            placeholder="Select..."
            allowClear
            options={suitable.map((m) => ({
              value: m.packingType,
              label: (
                <div className="flex justify-between">
                  <span>{m.packingType}</span>
                  <span className="text-gray-400">${Number(m.minimumCharge).toFixed(0)}</span>
                </div>
              ),
            }))}
          />
        );
      },
    },
    {
      title: "Qty",
      key: "quantity",
      width: 80,
      render: (_: any, record: InventoryTemplate) => {
        const selected = selectedItems.get(record.id);
        if (!selected) return null;

        return (
          <InputNumber
            size="small"
            min={1}
            value={selected.quantity}
            onChange={(v) => handleQuantityChange(record.id, v || 1)}
            className="w-16"
          />
        );
      },
    },
    {
      title: "Est. Cost",
      key: "cost",
      width: 100,
      render: (_: any, record: InventoryTemplate) => {
        const selected = selectedItems.get(record.id);
        if (!selected) return <span className="text-gray-400">—</span>;

        return (
          <span className="font-medium text-green-600">
            ${selected.totalCost.toFixed(2)}
          </span>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <div className="flex items-center">
          <Package size={20} className="mr-2 text-purple-600" />
          Select Inventory Templates
        </div>
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            {selectedItems.size > 0 && (
              <span>
                {totals.items} items • {totals.totalMinutes} min •{" "}
                <span className="font-medium text-green-600">${totals.totalCost.toFixed(2)}</span>
              </span>
            )}
          </div>
          <div className="space-x-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleAddToEstimate}
              disabled={selectedItems.size === 0}
              icon={<Calculator size={16} />}
            >
              Add {selectedItems.size > 0 ? `(${totals.items} items)` : "to Estimate"}
            </Button>
          </div>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search and Labor Rate */}
          <div className="flex gap-4">
            <Input
              placeholder="Search templates..."
              prefix={<Search size={16} className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1"
              allowClear
            />
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Labor:</span>
              <InputNumber
                size="small"
                value={hourlyRate}
                onChange={(v) => setHourlyRate(v || 90)}
                prefix="$"
                addonAfter="/hr"
                min={0}
                className="w-32"
              />
            </div>
          </div>

          {/* Templates Table */}
          {filteredTemplates.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredTemplates}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 8 }}
              rowClassName={(record) =>
                selectedItems.has(record.id) ? "bg-purple-50" : ""
              }
            />
          ) : (
            <Empty description="No templates found" />
          )}

          {/* Selected Items Summary */}
          {selectedItems.size > 0 && (
            <>
              <Divider className="my-3" />
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-3 flex items-center">
                  <DollarSign size={18} className="mr-2" />
                  Packing Cost Summary
                </h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-white rounded p-2 border text-center">
                    <div className="text-xs text-gray-500">Items</div>
                    <div className="font-bold">{totals.items}</div>
                  </div>
                  <div className="bg-white rounded p-2 border text-center">
                    <div className="text-xs text-gray-500">Materials</div>
                    <div className="font-bold">${totals.materialCost.toFixed(2)}</div>
                  </div>
                  <div className="bg-white rounded p-2 border text-center">
                    <div className="text-xs text-gray-500">Labor</div>
                    <div className="font-bold">${totals.laborCost.toFixed(2)}</div>
                  </div>
                  <div className="bg-green-100 rounded p-2 border border-green-300 text-center">
                    <div className="text-xs text-green-600">Total</div>
                    <div className="font-bold text-green-700">${totals.totalCost.toFixed(2)}</div>
                  </div>
                </div>
                <div className="mt-2 text-center text-sm text-gray-500">
                  <Clock size={14} className="inline mr-1" />
                  Estimated time: {totals.totalMinutes} minutes
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
};
